import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json", "X-Client-Platform": "web" },
  timeout: 60000,
});

// Auto-measure every API call's latency for the Admin Portal analytics.
api.interceptors.request.use((config) => {
  config.metadata = { start: Date.now() };
  return config;
});
const _emitApi = (config, status) => {
  try {
    // Lazy import avoids a circular dependency at module load.
    const { trackApiCall } = require("./analytics");
    const ms = Date.now() - ((config && config.metadata && config.metadata.start) || Date.now());
    let url = (config && config.url) || "";
    if (url && !url.startsWith("/api")) url = "/api" + (url.startsWith("/") ? url : "/" + url);
    url = url.split("?")[0];
    if (url) trackApiCall(url, ms, status);
  } catch (e) { /* never break requests */ }
};
api.interceptors.response.use(
  (res) => { _emitApi(res.config, res.status); return res; },
  (err) => { _emitApi(err && err.config, (err && err.response && err.response.status) || 0); return Promise.reject(err); }
);

// Stable per-browser device id (used for trusted-device auth on backend).
export function getDeviceId() {
  let id = localStorage.getItem("trata_web_device_id");
  if (!id) {
    id = "web-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("trata_web_device_id", id);
  }
  return id;
}

// Per-browser + per-phone trusted-device token (enables PIN login on return visits).
function deviceTokenKey(phone) {
  return `trata_web_device_token_${phone}`;
}
export function getDeviceToken(phone) {
  try {
    return localStorage.getItem(deviceTokenKey(phone)) || null;
  } catch (e) {
    return null;
  }
}
export function setDeviceToken(phone, token) {
  try {
    if (token) localStorage.setItem(deviceTokenKey(phone), token);
  } catch (e) {
    // ignore
  }
}

// ---------------- Auth ----------------
export const authApi = {
  checkUser: (phone) =>
    api.post(
      "/auth/check-user",
      { phone, device_id: getDeviceId(), device_token: getDeviceToken(phone) || undefined },
      { timeout: 8000 }
    ),
  sendOtp: (phone) => api.post("/auth/send-otp", { phone }),
  verifyOtp: (phone, otp) => api.post("/auth/verify-otp", { phone, otp }),
  selectRole: (phone, role) => api.post("/auth/select-role", { phone, role }),
  setPin: (phone, pin) => api.post("/auth/set-pin", { phone, pin }),
  verifyPin: (phone, pin) =>
    api.post("/auth/verify-pin", {
      phone,
      pin,
      device_id: getDeviceId(),
      device_token: getDeviceToken(phone) || undefined,
    }),
  registerTrustedDevice: (phone) =>
    api.post("/auth/trusted-device/register", {
      phone,
      device_id: getDeviceId(),
      device_name: "Web browser",
      platform: "web",
    }),
};

// ---------------- Customer / Profile ----------------
export const customerApi = {
  getProfile: (userId) => api.get(`/customer/profile/${userId}`),
  createProfile: (payload) => api.post("/customer/create-profile", payload),
  updateProfile: (userId, payload) => api.put(`/customer/profile/${userId}`, payload),
  getDependents: (userId) => api.get(`/customer/dependents/${userId}`),
  addDependent: (payload) => api.post("/customer/dependents/add", payload),
  updateDependent: (dependentId, payload) => api.put(`/customer/dependents/${dependentId}`, payload),
  deleteDependent: (dependentId) => api.delete(`/customer/dependents/${dependentId}`),
};

// ---------------- Consent (DPDP) ----------------
export const consentApi = {
  get: (phone) => api.get(`/account/consent/${phone}`),
  recordBulk: (payload) => api.post("/account/consent/record-bulk", payload),
};

// ---------------- Hospitals ----------------
export const hospitalApi = {
  browse: (params) => api.get("/patient/hospitals/browse", { params }),
  detail: (id) => api.get(`/patient/hospitals/${id}/detail`),
  services: (id, category) => api.get(`/patient/hospitals/${id}/services`, { params: category ? { category } : {} }),
  bookService: (hospitalId, serviceId, payload) =>
    api.post(`/patient/hospitals/${hospitalId}/services/${serviceId}/book`, payload),
};

// ---------------- Doctors ----------------
export const doctorApi = {
  search: (params) => api.get("/doctors/search", { params }),
};

// ---------------- Consultations ----------------
export const consultationApi = {
  book: (payload) => api.post("/consultations/book", payload),
  listPatient: (userId, group) => api.get(`/consultations/patient/${userId}`, { params: group ? { group } : {} }),
};

// ---------------- Video consultation (100ms) ----------------
export const rtcApi = {
  config: () => api.get("/rtc/config"),
  join: (bookingId, userId) => api.post(`/rtc/consultations/${bookingId}/join`, { userId }),
  end: (bookingId, userId) => api.post(`/rtc/consultations/${bookingId}/end`, { userId }),
  recordings: (bookingId) => api.get(`/rtc/consultations/${bookingId}/recordings`),
};

// ---------------- Bookings ----------------
export const bookingApi = {
  all: (userId) => api.get(`/patient/${userId}/all-bookings`),
};

// ---------------- Health records ----------------
export const healthApi = {
  records: (userId) => api.get(`/health/records/${userId}`),
  uploadBase64: (payload) => api.post("/health/records/upload-base64", payload),
  file: (recordId, userId) => api.get(`/health/records/${recordId}/file`, { params: { user_id: userId } }),
  status: (recordId, userId) => api.get(`/health/records/${recordId}/status`, { params: { user_id: userId } }),
  analyze: (recordId, userId, language = "en") => api.post(`/health/records/${recordId}/analyze`, { userId, language }),
  deleteRecord: (recordId, userId) => api.delete(`/health/records/${recordId}`, { params: { user_id: userId } }),
  medicationsConditions: (userId, patientId) => api.get(`/health/medications-conditions/${userId}`, { params: patientId ? { patient_id: patientId } : {} }),
  overallSummary: (userId) => api.get(`/health/overall-summary/${userId}`, { timeout: 60000 }),
  patientSummary: (userId, patientId) => api.get(`/health/patient-summary/${userId}`, { params: patientId ? { patient_id: patientId } : {}, timeout: 120000 }),
  updateRecordPatient: (recordId, userId, body) => api.put(`/health/records/${recordId}/patient`, body, { params: { user_id: userId } }),
};

// ---------------- Vital Map (organ-system health report) ----------------
export const vitalMapApi = {
  get: (userId, patient = "all") => api.get(`/vital-map/${userId}`, { params: { patient } }),
  insights: (userId, patient = "all") => api.post(`/vital-map/${userId}/insights`, {}, { params: { patient } }),
};

// ---------------- Lab test booking ----------------
export const labApi = {
  nearby: (lat, lng, radius = 25, city) => api.get("/labs/search/nearby", { params: { lat, lng, radius_km: radius, ...(city ? { city } : {}) } }),
  browse: (lat, lng, radius = 50, city) => api.get("/patient/labs/browse", { params: { lat, lng, radius_km: radius, ...(city ? { city } : {}) } }),
  aiRecommended: (userId, lat, lng) => api.get(`/labs/ai-recommended/${userId}`, { params: lat && lng ? { lat, lng } : {} }),
  details: (labId) => api.get(`/labs/${labId}`),
  book: (payload) => api.post("/labs/booking/create", payload),
  userBookings: (userId) => api.get(`/labs/booking/user/${userId}`),
};

// ---------------- Referral / My Hospital ----------------
export const referralApi = {
  resolve: (code) => api.get(`/referral/${encodeURIComponent(code)}`),
  savePreferred: (patientId, payload) => api.post(`/patient/${patientId}/preferred-hospital`, payload),
  myHospital: (patientId) => api.get(`/patient/${patientId}/my-hospital`),
  onboarded: (params) => api.get(`/hospitals/onboarded`, { params }),
  onboardedByType: (params) => api.get(`/providers/onboarded`, { params }),
  healthcareProviders: (patientId) => api.get(`/patient/${patientId}/healthcare-providers`),
  unpinProvider: (patientId, hospitalId) => api.delete(`/patient/${patientId}/preferred-hospital/${hospitalId}`),
};

// ---------------- Senior Care ----------------
export const guardianApi = {
  careSummary: (guardianId) => api.get(`/guardian/${guardianId}/care-summary`),
  logDependentVital: (dependentId, payload) => api.post(`/guardian/dependent/${dependentId}/vitals`, payload),
};

// ---------------- Medications ----------------
export const medicationApi = {
  list: (userId) => api.get(`/medications/${userId}`),
  add: (userId, payload) => api.post(`/medications/${userId}`, { user_id: userId, ...payload }),
  remove: (medicationId) => api.delete(`/medications/${medicationId}`),
  schedule: (userId) => api.get(`/medications/${userId}/schedule`),
  adherence: (userId, days = 7) => api.get(`/medications/${userId}/adherence`, { params: { days } }),
  logDose: (payload) => api.post(`/medications/log-dose`, payload),
};

// ---------------- Prescriptions, Pharmacy discovery, Reorders & Receipts ----------------
export const pharmacyApi = {
  prescriptions: (userId) => api.get(`/patient/${userId}/prescriptions`),
  nearby: (lat, lng, radius = 15) => api.get(`/patient/pharmacies/nearby`, { params: { lat, lng, radius_km: radius } }),
  createOrder: (userId, payload) => api.post(`/patient/${userId}/medicine-orders`, payload),
  orders: (userId) => api.get(`/patient/${userId}/medicine-orders`),
  cancelOrder: (orderId) => api.post(`/patient/medicine-orders/${orderId}/cancel`),
  receipts: (userId) => api.get(`/patient/${userId}/receipts`),
};

// ---------------- At-home care ----------------
export const atHomeApi = {  book: (payload) => api.post(`/at-home-care/book`, payload),
  bookings: (userId) => api.get(`/at-home-care/bookings/${userId}`),
};

// ---------------- Care plan vitals ----------------
export const carePlanApi = {
  vitals: (userId, days = 90) => api.get(`/care-plan/vitals/${userId}`, { params: { days } }),
  logVital: (payload) => api.post(`/care-plan/vitals/log`, payload),
};

// ---------------- Dr Sanjeevani ----------------
export const drApi = {
  chat: (payload) => api.post("/dr-sanjeevani/chat", payload),
  history: (userId) => api.get(`/dr-sanjeevani/history/${userId}`),
  newChat: (userId) => api.post("/dr-sanjeevani/new-chat", { user_id: userId }),
  nudges: (userId) => api.get(`/dr-sanjeevani/nudges/${userId}`),
};

// ---------------- Voice (STT / TTS) ----------------
export const voiceApi = {
  transcribe: (blob, language = "") => {
    const fd = new FormData();
    fd.append("file", blob, "audio.webm");
    fd.append("language", language);
    return api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  tts: (text, language = "en", voice) => api.post("/voice/tts", { text, language, voice }),
};

// ---------------- Emergency SOS & Ambulance ----------------
export const emergencyApi = {
  create: (payload) => api.post("/customer/emergency/create", payload),
  history: (userId) => api.get(`/customer/emergency-history/${userId}`),
  nearestHospital: (lat, lng) => api.get("/emergency/nearest-hospital", { params: { lat, lng } }),
  getCase: (id) => api.get(`/cases/${id}`),
  caseProviders: (id) => api.get(`/tracking/case/${id}/providers`),
  hospitalByEmergency: (id) => api.get(`/hospital-case/by-emergency/${id}`),
  cancelCase: (id) => api.post(`/customer/cancel-case/${id}`),
};

// ---------------- Diagnostic Centres (Imaging & Diagnostics) ----------------
export const diagnosticsApi = {
  listCentres: (params = {}) => api.get("/diagnostic-centres", { params }),
  getCentre: (id) => api.get(`/diagnostic-centres/${id}`),
  branches: (id) => api.get(`/diagnostic-centres/${id}/branches`),
  createBooking: (payload) => api.post("/diagnostic-bookings", payload),
  patientBookings: (patientId) => api.get(`/diagnostic-bookings/patient/${patientId}`),
};

// ---------------- ABDM / ABHA ----------------
export const abdmApi = {
  status: () => api.get("/abdm/status"),
  linked: (patientId) => api.get(`/abdm/abha/${patientId}`),
  aadhaarRequestOtp: (payload) => api.post("/abdm/abha/aadhaar/request-otp", payload),
  aadhaarVerifyOtp: (payload) => api.post("/abdm/abha/aadhaar/verify-otp", payload),
  mobileRequestOtp: (payload) => api.post("/abdm/abha/mobile/request-otp", payload),
  mobileVerifyOtp: (payload) => api.post("/abdm/abha/mobile/verify-otp", payload),
  createAddress: (payload) => api.post("/abdm/abha/address/create", payload),
  enrolSuggestions: (payload) => api.post("/abdm/abha/enrol/suggestions", payload),
  enrolAbhaAddress: (payload) => api.post("/abdm/abha/enrol/abha-address", payload),
  hipCareContexts: (userId) => api.get(`/abdm/hip/care-contexts/${userId}`),
  hipLink: (payload) => api.post("/abdm/hip/link", payload),
  hiuConsents: (userId) => api.get(`/abdm/hiu/consents/${userId}`),
  hiuRequestConsent: (payload) => api.post("/abdm/hiu/consent/request", payload),
  hiuFetchRecords: (consentId) => api.post(`/abdm/hiu/health-information/request/${consentId}`),
  hiuRecords: (txnId) => api.get(`/abdm/hiu/records/${txnId}`),
  loginRequestOtp: (payload) => api.post("/abdm/abha/login/request-otp", payload),
  loginVerifyOtp: (payload) => api.post("/abdm/abha/login/verify-otp", payload),
};

// ---------------- Notifications ----------------
export const notificationsApi = {
  list: (userId) => api.get(`/notifications/user/${userId}`),
  unreadCount: (userId) => api.get(`/notifications/user/${userId}/unread-count`),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: (userId) => api.put(`/notifications/user/${userId}/read-all`),
  remove: (id) => api.delete(`/notifications/${id}`),
};

// ---------------- Mental Wellness ----------------
export const wellnessApi = {
  consultants: (params) => api.get("/wellness/consultants", { params }),
  consultant: (id) => api.get(`/wellness/consultants/${id}`),
  book: (payload) => api.post("/wellness/book", payload),
  retreats: () => api.get("/wellness/retreats"),
  retreat: (id) => api.get(`/wellness/retreats/${id}`),
  helplines: () => api.get("/wellness/helplines"),
  resources: () => api.get("/wellness/resources"),
  resource: (id) => api.get(`/wellness/resources/${id}`),
  exercises: () => api.get("/wellness/exercises"),
  completeExercise: (id, userId) => api.post(`/wellness/exercises/${id}/complete`, { user_id: userId }),
  logMood: (payload) => api.post("/wellness/mood", payload),
  moodHistory: (userId) => api.get(`/wellness/mood/${userId}`),
  consultedProfessionals: (params) => api.get("/wellness/consulted-professionals", { params }),
  findSupport: (payload) => api.post("/wellness/find-support", payload),
  supportGroups: (params) => api.get("/wellness/support-groups", { params }),
  mySupportGroups: (userId) => api.get(`/wellness/support-groups/my/${userId}`),
  joinGroup: (groupId, payload) => api.post(`/wellness/support-groups/${groupId}/join`, payload),
  groupMessages: (groupId) => api.get(`/wellness/support-groups/${groupId}/messages`),
  postGroupMessage: (groupId, payload) => api.post(`/wellness/support-groups/${groupId}/messages`, payload),
  carePeerSupport: (userId) => api.get("/wellness/care-hub/peer-support", { params: { user_id: userId } }),
};

// ---------------- Location (reverse geocode via free OSM Nominatim) ----------------
export const locationApi = {
  reverseGeocode: (lat, lng) => api.get("/location/reverse-geocode", { params: { lat, lng } }),
  autocomplete: (input_text, session_token, location_bias) =>
    api.post("/places/autocomplete", { input_text, session_token, location_bias, country_code: "IN" }),
  placeDetails: (place_id, session_token) =>
    api.post("/places/details", { place_id, session_token }),
};
